import mixpanel from "mixpanel-browser";

import type {
  LeadsSearchParams,
  LinkedInProfile,
  SelectedFacetParam,
  WorkspaceAccount,
} from "common/types";

import type {
  BaseIssue,
  ErrorMessage,
  MaxLengthAction,
  MaxLengthIssue,
  PipeItem,
  SchemaWithPipe,
  StringSchema,
} from "valibot";

import WhitelabelPlaceholderImage from "assets/images/whitelabel/placeholder-logo.svg";
import WhitelabelPlaceholderImageSmall from "assets/images/whitelabel/placeholder-logo-small.svg";

type Entries<T> = { [K in keyof T]: [K, T[K]] }[keyof T];
export function typedObjectEntries<T extends object>(t: T): Entries<T>[] {
  return Object.entries(t) as Entries<T>[];
}
export function typedObjectKeys<T extends object>(t: T): Array<keyof T> {
  return Object.keys(t) as Array<keyof T>;
}
export function getDayName(dayNumber: number) {
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return dayNames[dayNumber];
}
export function getMonthName(monthNumber: number, shorten: boolean = false) {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return shorten
    ? monthNames[monthNumber].slice(0, 3)
    : monthNames[monthNumber];
}
export function addNumberOrdinal(number: number) {
  const numberAsString = number.toString();
  const lastTwoDigits = numberAsString.slice(-2);

  // Special cases: 11, 12, 13 always use "th"
  if (["11", "12", "13"].includes(lastTwoDigits)) {
    return `${number}th`;
  }

  const lastNumber = Number(numberAsString[numberAsString.length - 1]);

  let ordinal: string;

  switch (lastNumber) {
    case 1:
      ordinal = "st";
      break;
    case 2:
      ordinal = "nd";
      break;
    case 3:
      ordinal = "rd";
      break;
    default:
      ordinal = "th";
  }

  return `${number}${ordinal}`;
}
export function timeSince(unixMilliseconds: number) {
  const msPerMinute = 60 * 1000;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;
  const msPerMonth = msPerDay * 30;
  const msPerYear = msPerDay * 365;

  const elapsed = Date.now() - unixMilliseconds;

  if (elapsed < msPerMinute) {
    return "Just now";
  }
  if (elapsed < msPerHour) {
    const time = Math.round(elapsed / msPerMinute);
    return `${time} ${time > 1 ? "minutes" : "minute"} ago`;
  }
  if (elapsed < msPerDay) {
    const time = Math.round(elapsed / msPerHour);
    return `${time} ${time > 1 ? "hours" : "hour"} ago`;
  }
  if (elapsed < msPerMonth) {
    const time = Math.round(elapsed / msPerDay);
    return `${time} ${time > 1 ? "days" : "day"} ago`;
  }
  if (elapsed < msPerYear) {
    const time = Math.round(elapsed / msPerMonth);
    return `${time} ${time > 1 ? "months" : "month"} ago`;
  }
  const time = Math.round(elapsed / msPerYear);
  return `${time} ${time > 1 ? "years" : "year"} ago`;
}
export function formatTime(unixMilliseconds: number) {
  const date = new Date(unixMilliseconds);
  let hours: string | number = date.getHours();
  let minutes: string | number = date.getMinutes();

  hours = hours > 9 ? hours : `0${hours}`;
  minutes = minutes > 9 ? minutes : `0${minutes}`;

  return `${hours}:${minutes}`;
}
// Helper function to format time in 12-hour format with AM/PM
export function formatTimestamp(unixMilliseconds: number) {
  const date = new Date(unixMilliseconds);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";

  hours %= 12;
  hours = hours || 12; // the hour "0" should be "12"
  return `${hours}:${minutes} ${ampm}`;
}
export function formatDate(unixMilliseconds: number) {
  const dateObject = new Date(unixMilliseconds);
  const date = dateObject.getDate();
  const month = dateObject.getMonth() + 1;
  return `${date > 9 ? date : `0${date}`}.${
    month > 9 ? month : `0${month}`
  }.${dateObject.getFullYear()}`;
}
/* 0 -> 9,999 -- 6754
10,000 -> 99,999 -- 57.2k
100,000 -> 999,999 -- 320k
1,000,000 -> 9,999,999 -- 2.35M
10,000,000 -> 99,999,999 -- 22.5M
100,000,000+ -- 212M */
export function formatNumber(number: number) {
  if (number < 10000) {
    return number.toString();
  }
  if (number < 1000000) {
    return `${Number((number / 1000).toFixed(2))}k`;
  }
  return `${Number((number / 1000000).toFixed(2))}M`;
}
// returns location | occupation or occupation if location is not defined
export function getProfileSubtitle(
  profile: Pick<LinkedInProfile, "location" | "occupation">,
) {
  if (profile.location) {
    return `${profile.location.name} | ${profile.occupation}`;
  }
  return profile.occupation;
}
export function formatLastUpdateAt(unixTimestamp: number) {
  if (Date.now() - unixTimestamp < 60 * 1000) {
    return "just now";
  }
  const today = new Date();
  today.setHours(0, 0, 0);

  if (unixTimestamp > today.getTime()) {
    return formatTimestamp(unixTimestamp);
  }
  const date = new Date(unixTimestamp);
  return `${getMonthName(date.getMonth(), true)} ${date.getDate()}`;
}
export function getSchemaMaxLength(
  schema: SchemaWithPipe<
    [StringSchema<string>, ...PipeItem<unknown, unknown, BaseIssue<unknown>>[]]
  >,
) {
  for (let i = 0; i < schema.pipe.length; i += 1) {
    const validation = schema.pipe[i];
    if (validation.type === "max_length") {
      return (
        validation as MaxLengthAction<
          string,
          number,
          ErrorMessage<MaxLengthIssue<string, number>>
        >
      ).requirement;
    }
  }
  throw Error(`No max_length rule found in schema: ${schema}`);
}
// returns false for preview and development environments
export function isProductionEnvironment() {
  return ["production", "staging"].includes(import.meta.env.MODE);
}

function getDaySuffix(day: number) {
  if (day >= 11 && day <= 13) return "th";

  const suffixes: { [key: number]: string } = { 1: "st", 2: "nd", 3: "rd" };
  return suffixes[day % 10] || "th";
}

export function formatShortDate(unixMilliseconds: number) {
  const date = new Date(unixMilliseconds);
  const day = date.getDate();
  const suffix = getDaySuffix(day);

  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  return formattedDate.replace(day.toString(), `${day}${suffix}`);
}

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

export function getCountryName(countryCode: string) {
  return regionNames.of(countryCode.toUpperCase());
}

export function decodeProxyUrl(proxyUrl: string) {
  const [protocol, address] = proxyUrl.split("://");

  if (address.includes("@")) {
    const [auth, actualAddress] = address.split("@");
    const [username, password] = auth.split(":");

    return { protocol, username, password, address: actualAddress };
  }
  return { protocol, address };
}

// proxy is either a custom proxy id or a country code
export function getProxyConfigPayload(selectedProxy: string) {
  if (selectedProxy.length === 2) {
    return {
      "location.countryISO": {
        $eq: selectedProxy,
      },
      "provider.name": {
        $eq: "nord",
      },
    };
  }
  return { custom_proxy_id: selectedProxy };
}

// example: 1-10 of 15, {from}-{to} of {totalItemCount}
export function getPaginationInfoText(
  selectedIndex: number,
  pageSize: number,
  totalItemCount: number,
) {
  if (totalItemCount <= 0) {
    return "0-0 of 0";
  }

  if (pageSize <= 0) {
    return `1-${totalItemCount} of ${totalItemCount}`;
  }

  const safeSelectedIndex = Math.max(0, selectedIndex);
  const from = Math.min(totalItemCount, safeSelectedIndex * pageSize + 1);
  const to = Math.min(totalItemCount, from + pageSize - 1);

  return `${from}-${to} of ${totalItemCount}`;
}

export function getWhitelabelCustomization() {
  // this value will always be set for client logins
  const whitelabelCustomization =
    window.whitelabelCustomization === "%WHITELABEL_CUSTOMIZATION%"
      ? undefined
      : window.whitelabelCustomization;

  if (whitelabelCustomization) {
    if (!whitelabelCustomization.favicon) {
      whitelabelCustomization.favicon = WhitelabelPlaceholderImageSmall;
    }
    if (!whitelabelCustomization.logo) {
      whitelabelCustomization.logo = WhitelabelPlaceholderImage;
    }
  }

  return whitelabelCustomization;
}

export function trackMetaPixel(
  type: "track" | "trackCustom",
  event: string,
  params?: Record<string, unknown>,
) {
  if (!getWhitelabelCustomization() && typeof window.fbq === "function") {
    window.fbq(type, event, params);
  }
}

export function trackMixpanel(eventName: string, source?: string) {
  if (isProductionEnvironment()) {
    mixpanel.track(eventName, source && { source });
  }
}

// returns parsed search params for server requests
export function parseLeadsSearchParams(
  searchParams: LeadsSearchParams<SelectedFacetParam>,
) {
  return typedObjectEntries(searchParams).reduce(
    (accumulator, [key, value]) => {
      if (key === "keywords") {
        accumulator[key] = value.trim();
      } else if (key === "favorite") {
        accumulator[key] = value;
      } else {
        accumulator[key] = value.flatMap(({ id }) => id.split(","));
      }
      return accumulator;
    },
    {} as LeadsSearchParams<string>,
  );
}

//favorite and keywords don't have a facet
export function getSearchParamFacetKey(
  searchParam: keyof Omit<LeadsSearchParams<never>, "favorite" | "keywords">,
) {
  return searchParam === "education" ? "schools" : searchParam;
}

export function getChromeExtensionUrl(extensionId: string) {
  return `https://chromewebstore.google.com/detail/${extensionId}`;
}

export function getWorkerAccountIssueOrder(account: WorkspaceAccount): number {
  // User account without a license — no seat assigned.
  if (account.type === "user" && !account.license) return 1;

  // Session not active - disconnected.
  if (account.state !== "LoggedIn") return 2;

  // Connected with seat — ok.
  return 0;
}

export function compareWorkerAccounts(
  a: WorkspaceAccount,
  b: WorkspaceAccount,
): number {
  return (
    getWorkerAccountIssueOrder(a) - getWorkerAccountIssueOrder(b) ||
    a.full_name.localeCompare(b.full_name)
  );
}

export function getAccountStateLabel(
  account: WorkspaceAccount,
  isUsingNote: boolean,
  isInCooldown: boolean,
) {
  const { state, license, type, premium } = account;

  if (type === "user" && !license) {
    return "No seat";
  } else if (state === "LoggedOut") {
    return "Disconnected";
  } else if (isUsingNote && !premium) {
    return "No premium";
  } else if (isInCooldown) {
    return "In cooldown";
  }
}
